import type { PaymentMethod, WorkCreateInput } from "./api";

export const WORK_DRAFT_TTL_MS = 8 * 60 * 60 * 1000;
export const WORK_DRAFT_PREFIX = "avtodaftar_mechanic_work_draft_v1:";
export const WORK_HISTORY_BOUNDARY_KEY = "avtodaftar_mechanic_work_history_boundary_v1";

export type DraftPart = {
  productId: number;
  name: string;
  unit: string;
  quantity: string;
  price: string;
};

export type DraftService = {
  serviceId: number;
  serviceName: string;
  price: string;
  /** Optional so drafts saved before per-service reminders remain readable. */
  reminderText?: string;
};
export type DraftPayment = { method: PaymentMethod; amount: string; amountEdited?: boolean };

export type PendingWorkAttempt = {
  payload: WorkCreateInput;
  replayUsed: boolean;
  /** True once the original request may already have committed. */
  outcomeUnknown?: boolean;
};

export type WorkDraft = {
  version: 1;
  userId: number;
  workshopId: number;
  createdAt: number;
  operationUuid: string;
  clientId: number;
  clientName: string;
  clientPhone: string;
  carId: number;
  plateNumber: string;
  carModel: string | null;
  serviceId: number | null;
  serviceName: string;
  price: string;
  services?: DraftService[];
  parts: DraftPart[];
  note: string;
  reminderNote: string;
  /** New web drafts replace the template; missing means a legacy appended note. */
  reminderMode?: "override";
  reminderEdited?: boolean;
  paid: string;
  debtDueDays: string;
  paymentMethod: PaymentMethod;
  payments?: DraftPayment[];
  pendingAttempt?: PendingWorkAttempt;
};

export type DraftReadResult = { draft: WorkDraft | null; expired: boolean };

export function workDraftKey(userId: number): string {
  return `${WORK_DRAFT_PREFIX}${userId}`;
}

export function readWorkDraft(
  userId: number,
  workshopId: number,
  now = Date.now(),
): DraftReadResult {
  const key = workDraftKey(userId);
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(key);
  } catch {
    return { draft: null, expired: false };
  }
  if (!raw) return { draft: null, expired: false };
  try {
    const value = JSON.parse(raw) as WorkDraft;
    const valid = value.version === 1 && value.userId === userId &&
      value.workshopId === workshopId && Number.isFinite(value.createdAt) &&
      typeof value.operationUuid === "string" && value.operationUuid.length > 0;
    if (!valid) {
      try { window.localStorage.removeItem(key); } catch { /* storage is unavailable */ }
      return { draft: null, expired: false };
    }
    // Editable drafts expire. A request being reconciled must retain its
    // operation identity until the server gives a conclusive answer.
    if (!value.pendingAttempt && now >= value.createdAt + WORK_DRAFT_TTL_MS) {
      try { window.localStorage.removeItem(key); } catch { /* storage is unavailable */ }
      return { draft: null, expired: true };
    }
    const normalized: WorkDraft = {
      ...value,
      services: Array.isArray(value.services)
        ? value.services
        : value.serviceId && value.serviceName
          ? [{ serviceId: value.serviceId, serviceName: value.serviceName, price: value.price }]
          : [],
      payments: Array.isArray(value.payments)
        ? value.payments
        : [{ method: value.paymentMethod ?? "naqd", amount: value.paid ?? "" }],
      parts: Array.isArray(value.parts)
        ? value.parts.map((part) => ({ ...part, quantity: String(part.quantity) }))
        : [],
    };
    return { draft: normalized, expired: false };
  } catch {
    try { window.localStorage.removeItem(key); } catch { /* storage is unavailable */ }
    return { draft: null, expired: false };
  }
}

export function writeWorkDraft(draft: WorkDraft): boolean {
  try {
    window.localStorage.setItem(workDraftKey(draft.userId), JSON.stringify(draft));
    return true;
  } catch {
    return false;
  }
}

export function clearWorkDraft(userId: number): void {
  try {
    window.localStorage.removeItem(workDraftKey(userId));
  } catch {
    // In-memory form state remains usable when persistent storage is blocked.
  }
}

/** Clear an ordinary form on logout, but never discard an unresolved write. */
export function clearWorkDraftUnlessPending(userId: number): boolean {
  const key = workDraftKey(userId);
  try {
    const raw = window.localStorage.getItem(key);
    if (raw) {
      const value = JSON.parse(raw) as Partial<WorkDraft>;
      if (value.pendingAttempt) return false;
    }
    window.localStorage.removeItem(key);
    return true;
  } catch {
    return false;
  }
}

export function remainingDraftMs(draft: WorkDraft, now = Date.now()): number {
  if (draft.pendingAttempt) return Number.POSITIVE_INFINITY;
  return Math.max(0, draft.createdAt + WORK_DRAFT_TTL_MS - now);
}

export function markWorkHistoryBoundary(): void {
  const boundary = readBoundary();
  boundary.epoch += 1;
  writeBoundary(boundary);
}

export function hasWorkHistoryBoundary(): boolean {
  return readBoundary().epoch > 0;
}

type HistoryBoundary = { epoch: number; entries: Record<string, number> };
let fallbackBoundary: HistoryBoundary = { epoch: 0, entries: {} };
let memoryOnlyBoundary = false;
function readBoundary(): HistoryBoundary {
  if (memoryOnlyBoundary) return fallbackBoundary;
  try {
    const raw = sessionStorage.getItem(WORK_HISTORY_BOUNDARY_KEY);
    if (!raw) return { epoch: 0, entries: {} };
    if (raw === "1") return { epoch: 1, entries: {} }; // existing Stage 7 sessions
    return JSON.parse(raw) as HistoryBoundary;
  } catch { memoryOnlyBoundary = true; return fallbackBoundary; }
}
function writeBoundary(boundary: HistoryBoundary) {
  fallbackBoundary = boundary;
  try { sessionStorage.setItem(WORK_HISTORY_BOUNDARY_KEY, JSON.stringify(boundary)); } catch { memoryOnlyBoundary = true; }
}
export function historicalEntryBlocked(key: string, navigationType: string): boolean {
  const boundary = readBoundary();
  return boundary.epoch > 0 && navigationType === "POP" && boundary.entries[key] !== boundary.epoch;
}
export function rememberHistoryEntry(key: string) {
  const boundary = readBoundary();
  boundary.entries[key] = boundary.epoch;
  writeBoundary(boundary);
}
